/**
 * Jyotish Tarang — Buy Credit Modal (Phase 5, 14 Jul 2026, one-time-payments)
 * Shared ₹99 pay-per-use purchase modal for all 5 credit-gated actions
 * (chart, ai_question, milan, muhurta, rectification). Parameterized by
 * creditType rather than five bespoke modals. Razorpay Orders-API flow is
 * copied directly from ReportPage.tsx::handleBuy() -- same order/verify
 * shape, just pointed at /api/credits/* (backend/api/credits_api.py,
 * Phase 2) instead of /api/reports/*. Controlled component: caller owns
 * `open` state and re-runs the original action from `onPurchased()`.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

declare global { interface Window { Razorpay: any; } }

function getToken() {
  return sessionStorage.getItem('access_token') || localStorage.getItem('access_token') || '';
}
async function apiFetch(url: string, options?: any) {
  const token = getToken();
  const h: any = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers: { ...h, ...(options?.headers || {}) } });
}

interface Props {
  open: boolean;
  creditType: string;
  priceInr?: number;
  onClose: () => void;
  onPurchased: () => void;
}

export default function BuyCreditModal({ open, creditType, priceInr = 99, onClose, onPurchased }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const CREDIT_LABELS: Record<string, { icon: string; title: string; blurb: string }> = {
    chart:         { icon: '📊', title: t('buyCredit.chart.title'),         blurb: t('buyCredit.chart.blurb') },
    ai_question:   { icon: '🔮', title: t('buyCredit.aiQuestion.title'),    blurb: t('buyCredit.aiQuestion.blurb') },
    milan:         { icon: '💍', title: t('buyCredit.milan.title'),         blurb: t('buyCredit.milan.blurb') },
    muhurta:       { icon: '🎯', title: t('buyCredit.muhurta.title'),       blurb: t('buyCredit.muhurta.blurb') },
    rectification: { icon: '🕐', title: t('buyCredit.rectification.title'), blurb: t('buyCredit.rectification.blurb') },
  };
  const meta = CREDIT_LABELS[creditType] || { icon: '✨', title: t('buyCredit.default.title'), blurb: t('buyCredit.default.blurb') };

  if (!open) return null;

  async function handleBuy() {
    setLoading(true); setError('');
    try {
      const orderRes = await apiFetch('/api/credits/order', {
        method: 'POST', body: JSON.stringify({ credit_type: creditType }),
      });
      const orderData = await orderRes.json();
      if (!orderData.order_id) throw new Error(orderData.error || t('buyCredit.errors.couldNotStart'));

      const options: any = {
        key: orderData.key_id, order_id: orderData.order_id, amount: orderData.amount,
        currency: orderData.currency || 'INR', name: 'Jyotish Tarang',
        description: `${meta.title} — one-time ₹${priceInr}`,
        image: '/static/img/logo.png',
        theme: { color: '#FF9933' },
        handler: async (response: any) => {
          try {
            const verRes = await apiFetch('/api/credits/verify', {
              method: 'POST',
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                credit_type: creditType,
              }),
            });
            const verData = await verRes.json();
            if (verData.status !== 'unused') throw new Error(verData.error || t('buyCredit.errors.verificationFailed'));
            setLoading(false);
            onPurchased();
          } catch (err: any) {
            setLoading(false);
            setError(err.message || t('buyCredit.errors.verificationFailedContact'));
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };

      if (!window.Razorpay) {
        await new Promise<void>((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://checkout.razorpay.com/v1/checkout.js';
          s.onload = () => resolve();
          s.onerror = () => reject(new Error(t('buyCredit.errors.gatewayLoadFailed')));
          document.head.appendChild(s);
        });
      }
      const rp = new window.Razorpay(options);
      rp.open();
    } catch (err: any) {
      setLoading(false);
      setError(err.message || t('buyCredit.errors.couldNotStart'));
    }
  }

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, zIndex: 2001,
        background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
      }}/>
      <div style={{
        position: 'fixed', top: '50%', left: '50%', zIndex: 2002,
        transform: 'translate(-50%,-50%)', background: '#fff', borderRadius: 14,
        width: 'min(420px, 92vw)', overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{
          background: 'var(--indigo)', padding: '16px 20px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#F4C430' }}>{meta.icon} {meta.title}</div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
            fontSize: 20, cursor: 'pointer', lineHeight: 1,
          }}>✕</button>
        </div>
        <div style={{ padding: '20px' }}>
          <p style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 16 }}>{meta.blurb}</p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 16 }}>
            <span style={{ fontSize: 26, fontWeight: 700, color: 'var(--sfb)', fontFamily: 'var(--font-mono)' }}>₹{priceInr}</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{t('buyCredit.oneTimeNoSub')}</span>
          </div>
          {error && <div className="info-box info-red" style={{ marginBottom: 12, fontSize: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={onClose} className="btn btn-ghost" disabled={loading}>{t('common.cancel')}</button>
            <button onClick={handleBuy} className="btn btn-primary" disabled={loading}>
              {loading ? t('buyCredit.openingPayment') : t('buyCredit.payButton', { price: priceInr })}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
